import { useRef, useState } from "react";
import { Mic, Square } from "lucide-react";

/**
 * Reusable in-chat voice recorder. Uses MediaRecorder API.
 * Calls onComplete({ blob, mimeType, durationSec }) when the user stops.
 */
export const VoiceRecorder = ({ onComplete, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a widely-supported mimeType
      const supported = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
      const mr = new MediaRecorder(stream, supported ? { mimeType: supported } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const dur = (Date.now() - startedAtRef.current) / 1000;
        stream.getTracks().forEach((t) => t.stop());
        if (tickRef.current) clearInterval(tickRef.current);
        setRecording(false);
        setElapsed(0);
        if (blob.size > 0) onComplete?.({ blob, mimeType: blob.type, durationSec: dur });
      };
      mediaRef.current = mr;
      mr.start();
      startedAtRef.current = Date.now();
      setRecording(true);
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)), 250);
    } catch (e) {
      alert("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
    }
  };

  const stop = () => {
    try { mediaRef.current?.stop(); } catch { /* noop */ }
  };

  if (recording) {
    return (
      <button
        type="button"
        data-testid="voice-stop"
        onClick={stop}
        className="h-9 px-3 rounded-full bg-red-500 text-white flex items-center gap-1.5 animate-pulse"
        title="Detener grabación"
      >
        <Square className="w-3 h-3 fill-current" />
        <span className="text-[10px] font-mono">{elapsed}s</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      data-testid="voice-record"
      onClick={start}
      disabled={disabled}
      className="h-9 w-9 rounded-full bg-black/40 border border-white/10 hover:border-[#d4af37]/50 text-neutral-300 hover:text-[#d4af37] flex items-center justify-center disabled:opacity-40"
      title="Grabar nota de voz"
    ><Mic className="w-4 h-4" /></button>
  );
};

/** Convert a Blob to base64 (no data: prefix). */
export const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => {
    const result = fr.result || "";
    const i = String(result).indexOf(",");
    resolve(i >= 0 ? String(result).slice(i + 1) : String(result));
  };
  fr.onerror = reject;
  fr.readAsDataURL(blob);
});
